/* ManageOrgsComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./ManageOrgsRenderer");
const _ = require("lodash");
const GSO_ID = "565a65db-54e7-4461-a954-f0f38f310e19";

class ManageOrgsComponent extends EventEmitter {
    constructor(
        orgService,
        RetentionPolicy,
        userService,
        userSession,
        uiUtils,
        steve,
        logger,
        router,
        HubbyService,
        modalUserFeedback,
        userLocalStorage,
        storageFactory,
        systemConfig,
        CredentialsEntityDef,
        TenantEntity
    ) {
        super();
        this._orgService = orgService;
        this._userService = userService;
        this._userSession = userSession;
        this._retentionPolicyEntity = RetentionPolicy;
        this._uiUtils = uiUtils;
        this._renderer = new Renderer(this, this._uiUtils);
        this._steve = steve;
        this._logger = logger;
        this._router = router;
        this._hubbyService = HubbyService;
        this._modalUserFeedback = modalUserFeedback;
        this._localStorage = userLocalStorage;
        this._storageFactory = storageFactory;
        this._systemConfig = systemConfig;
        this._credentialsEntityDef = CredentialsEntityDef;
        this._credentialsList = [];
        this._credentialsOrgId = null;
        this._tenantEntity = TenantEntity;
        this._pwLengthWarning = false;

        //System Developers are the only ones able to install packages, so we need to know that info in this cmpnt.
        //this._isUserSystemDev = (this._steve.user.session.info.actionGroupName === 'System Developer');

        this._allOrgs = undefined;

        //Currently selected orgId
        this._selectedOrgId = null;
        this._selectedOrgInfo = null;

        //The data we need to get this party started.
        this._getOrgsProm = this._getOrganizations();
        this._getSelectedOrgInfoProm = null;
        this._getUserInfoProm = this.fetchUserInfo();
        this._retrieveAllCredentialsProm = this._retrieveAllCredentials();
        if (this.lastSelectedOrgId) {
            this.setSelectedOrgId(this.lastSelectedOrgId, true);
        }

        this._systemConfigEntity = undefined;
        this._samloAuthExists = false;

        this._screenDisplayType = "edit";

        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    _getSelectedOrgInfo() {
        if (this._selectedOrgId) {
            return this._orgService.getById({id: this._selectedOrgId});
        }
    }

    _getOrganizations() {
        return this._orgService.getOrganizations({}).then(
            (result) => {
                this.allOrgs = result;
            },
            (err) => {
                this._logger.warn(
                    {error: err},
                    "ManageOrgsComponent._getOrganizations() I am unable to request your user/org info."
                );
            }
        );
    }

    async _saveSelectedOrganization(orgInfo, pwInfo) {
        let updateOrgObj = orgInfo;
        const newMinLength =
            pwInfo.minUpperCaseChar +
            pwInfo.minLowerCaseChar +
            pwInfo.minNumericChar +
            pwInfo.minSpecialChar;
        if (newMinLength > this._passwordPolicy.minPasswordLength) {
            this._pwLengthWarning = true;
        }
        // TODO - Fix dirty check before applying these
        // if(_.get(updateObj, 'radius.accountingRemoved', false)) {
        //     updateObj.radius.accountingHost = undefined;
        //     updateObj.radius.accountingPort = undefined;
        //     _.unset(updateObj, 'radius.accountingRemoved');
        // }

        if (!_.isEqual(this._tenant.getPasswordPolicy(), pwInfo)) {
            this._tenant.setPasswordPolicy(pwInfo);
            await this._tenant.update();
        }
        updateOrgObj.id = this._selectedOrgId;
        return this._orgService.update(updateOrgObj);
    }

    _createOrganization(orgInfo) {
        return this._orgService.create(orgInfo);
    }

    _installGluwareSolutionPackages(orgId, {Success, Progress, Failure} = {}) {
        if (orgId) {
            this._hubbyService.packageInstallAllFromRepo(
                {
                    orgId: orgId,
                    repoId: "1356aee0-3215-4e8b-8a31-69b2362e167f",
                },
                {Success, Progress, Failure}
            );
        }
    }

    _deleteSelectedOrganization(id) {
        return this._orgService.delete({id: id});
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get modalUserFeedback() {
        return this._modalUserFeedback;
    }

    get logger() {
        return this._logger;
    }

    get router() {
        return this._router;
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterListeners();
        return this._renderer._shutdown().then(
            () => {
                this._isUserSystemDev = undefined;
                this._myUserInfo = undefined;
                this._allOrgs = undefined;
                this._selectedOrgId = undefined;
                this._selectedOrgInfo = undefined;
                this._screenDisplayType = undefined;
                this._renderer = undefined;
                this._orgService = undefined;
                this._userSession = undefined;
                this._steve = undefined;
                this._localStorage = undefined;
                this._storageFactory = undefined;
                this._logger = undefined;
            },
            (err) => {
                this._logger.warn(
                    {error: err},
                    "ManageOrgsComponent.shutdown() Error shutting down ManageOrgsComponent"
                );
            }
        );
    }
    get userActiveOrganization() {
        return this._userSession.activeOrganization;
    }

    get allOrgs() {
        return this.getAllOrgs();
    }

    set allOrgs(orgsObj) {
        this.setAllOrgs(orgsObj);
    }

    getAllOrgs() {
        return this._allOrgs;
    }

    setAllOrgs(orgsObj) {
        this._allOrgs = orgsObj;
    }

    get lastSelectedOrgId() {
        return _.get(this._router.routeData, "EditOrgScreen.selectedOrgId");
    }

    set selectedOrgId(orgId) {
        this.setSelectedOrgId(orgId);
    }

    setSelectedOrgId(orgId, silent) {
        this._selectedOrgId = orgId;
        const credentialsOrgId = this._claimCredentialsOrg(orgId);
        if (this.lastSelectedOrgId !== orgId) {
            this._router.updateRouteInfo({rd: {EditOrgScreen: {selectedOrgId: orgId}}});
        }
        this._selectedOrgInfo = null;
        this._getSelectedOrgInfoProm = this._getSelectedOrgInfo()
            .then((result) => {
                this._selectedOrgInfo = result;
                return this.changeSelectedTenantId();
            })
            .then(() => this._retrieveAllCredentials())
            .then(() => {
                if (!silent) {
                    this.emit(
                        "ModelSelectedOrgInfoChange",
                        this._selectedOrgInfo,
                        this._passwordPolicy
                    );
                }
            })
            .catch((err) => {
                if (this._isCurrentCredentialsOrg(credentialsOrgId)) {
                    this._credentialsList = [];
                }
                this._logger.warn(
                    {error: err},
                    "ManageOrgsComponent.setSelectedOrgId() Error setting selected orgId "
                );
            });
    }

    async changeSelectedTenantId() {
        try {
            this._tenant = await this._tenantEntity.loadTenant(this._selectedOrgId, {});
            this._passwordPolicy = this._tenant.getPasswordPolicy() || {
                minPasswordLength: 12,
                minLowerCaseChar: 0,
                minNumericChar: 0,
                minSpecialChar: 0,
                minUpperCaseChar: 0,
            };

            if (!this._tenant.getPasswordPolicy()) {
                this._tenant.setPasswordPolicy(this._passwordPolicy);
                await this._tenant.update();
                console.warn("No password policy defined, setting to defaults");
            }
        } catch (error) {
            console.error("Error in changeSelectedTenantId:", error);
        }
    }

    get selectedOrgId() {
        return this._selectedOrgId;
    }

    get selectedOrgInfo() {
        return this._selectedOrgInfo;
    }

    get activeOrgId() {
        return this._userSession.activeOrganization.id;
    }

    whenModelIsReady() {
        let promArr = [this._getOrgsProm, this._getUserInfoProm, this._retrieveAllCredentialsProm];
        return Promise.all(promArr)
            .then(() => this.getSystemConfig())
            .then((systemEntity) => {
                this._systemConfigEntity = systemEntity;

                const samlConfig = this._systemConfigEntity.getSamlConfig(),
                    oauthConfig = this._systemConfigEntity.getOauthConfig();
                this._isDCEnabled =
                    this._systemConfigEntity._pristineProperties.enableDistributionCenter;

                if (samlConfig && samlConfig.enable) {
                    this._samloAuthExists = true;
                } else if (oauthConfig && oauthConfig.enable) {
                    this._samloAuthExists = true;
                }
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "ManageOrgsComponent.whenModelIsReady() Error starting up org and/or tenant data"
                );
            });
    }

    whenOrgModelIsReady() {
        return this._getSelectedOrgInfoProm;
    }

    get screenDisplayType() {
        return this._screenDisplayType;
    }

    set screenDisplayType(typeName) {
        this._screenDisplayType = typeName;
        this.renderer.render();
    }

    organizationChanged(orgObj) {
        const newOrgId = _.get(orgObj, "id") || this.activeOrgId,
            credentialsOrgId = this._claimCredentialsOrg(newOrgId);
        this._getOrgsProm = this._getOrganizations();
        return this._getOrgsProm
            .then(() => {
                //Silent: the render below rebuilds the screen with the new org's data.
                this.setSelectedOrgId(newOrgId, true);
                return this.renderer.render();
            })
            .catch((err) => {
                if (this._isCurrentCredentialsOrg(credentialsOrgId)) {
                    this._credentialsList = [];
                }
                this._logger.warn(
                    {error: err},
                    "ManageOrgsComponent.organizationChanged() Error reloading for new active org"
                );
            });
    }

    saveSelectedOrg(orgObj, pwInfo) {
        this._saveSelectedOrganization(orgObj, pwInfo)
            .then((result) => {
                this._selectedOrgInfo = result;
                return this._getOrganizations();
            })
            .then(() => {
                this.emit("SaveSuccess");
                this.renderer.render();
            })
            .catch((err) => {
                this.emit("SaveError", err);
            });
    }

    testLDAPConnection(ldapConfig) {
        let requestPayload = {
            ldapConfig: ldapConfig,
        };
        if (this.screenDisplayType === "edit") {
            requestPayload.id = this.selectedOrgId;
        }
        this._uiUtils.writeMessage("Testing LDAP Connection...");
        return this._orgService
            .testLdapConnection(requestPayload)
            .then((result) => {
                this._uiUtils.clearMessage();
                if (result.error) {
                    this.emit("TestLdapError", result.error);
                } else {
                    this.emit("testLdapResult", result);
                }
            })
            .catch((error) => {
                this._uiUtils.clearMessage();
                this.emit("TestLdapError", error);
            });
    }

    testRADIUSConnection(radiusConfig) {
        let requestPayload = {
            radiusConfig: radiusConfig,
        };
        if (this.screenDisplayType === "edit") {
            requestPayload.id = this.selectedOrgId;
        }
        this._uiUtils.writeMessage("Testing RADIUS Connection...");
        return this._orgService
            .testRadiusConnection(requestPayload)
            .then((result) => {
                this._uiUtils.clearMessage();
                if (result.error) {
                    this.emit("TestRadiusError", result.error);
                } else {
                    this.emit("testRadiusResult", result);
                }
            })
            .catch((error) => {
                this._uiUtils.clearMessage();
                this.emit("TestRadiusError", error);
            });
    }

    createOrg(orgObj) {
        let resultOrg;
        this._createOrganization(orgObj)
            .then((result) => {
                resultOrg = result;
                this._selectedOrgId = result.id;
                this._selectedOrgInfo = result;
                return this._getOrganizations();
            })
            .then(() => {
                this._screenDisplayType = "edit";
                this.emit("CreateSuccess", resultOrg);
            })
            .catch((err) => {
                this.emit("CreateError", err);
            });
    }

    loadPackages(orgId) {
        this._installGluwareSolutionPackages(orgId, {
            Success: this.emit.bind(this, "LoadPackagesSuccess"),
            Progress: this.emit.bind(this, "LoadPackagesProgress"),
            Failure: this.emit.bind(this, "LoadPackagesFailure"),
        });
    }

    deleteSelectedOrg() {
        this._deleteSelectedOrganization(this._selectedOrgId)
            .then(() => {
                this._selectedOrgId = null;
                this._selectedOrgInfo = null;
                return this._getOrganizations();
            })
            .then(() => {
                this.emit("DeleteSuccess");
                return this.renderer.render();
            })
            .catch((err) => {
                this._logger.warn({error: err}, "ManageOrgsComponent.deleteSelectedOrg() error");
                this.emit("DeleteError", err);
            });
    }

    isSystemDev() {
        return this._isUserSystemDev;
    }

    fetchUserInfo() {
        return this._getUserInfo()
            .then((result) => {
                this._myUserInfo = result;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "ManageOrgsComponent.fetchUserInfo() error getting user info for org screen"
                );
            });
    }

    get userInfo() {
        return this.getUserInfo();
    }

    getUserInfo() {
        return this._myUserInfo;
    }

    isEditedOrgSelected() {
        return this.selectedOrgInfo.id === this._userSession.activeOrganization.id;
    }

    getSystemConfig() {
        return this._systemConfig.loadSystemConfig(GSO_ID);
        // return sysDetailsFeed.whenInitialized();
    }

    //Marks orgId as the credentials request in flight. Older requests then lose the
    //race on purpose, see _isCurrentCredentialsOrg().
    _claimCredentialsOrg(orgId) {
        this._credentialsOrgId = orgId || this.activeOrgId;
        return this._credentialsOrgId;
    }

    //Only the most recently requested org may write _credentialsList, so a slow
    //response cannot overwrite - or clear - a newer org's credentials.
    _isCurrentCredentialsOrg(orgId) {
        return orgId === this._credentialsOrgId;
    }

    //Credentials must come from the org being edited - a credential from another org
    //cannot be resolved by that org's LDAP/RADIUS config.
    _retrieveAllCredentials() {
        const orgId = this._claimCredentialsOrg(this._selectedOrgId);
        return this._credentialsEntityDef
            .loadLabelValues(orgId)
            .then((credentials) => {
                if (this._isCurrentCredentialsOrg(orgId)) {
                    this._credentialsList = credentials || [];
                }
            })
            .catch((err) => {
                if (this._isCurrentCredentialsOrg(orgId)) {
                    this._credentialsList = [];
                }
                this._logger.warn(
                    {error: err},
                    "ManageOrgsComponent._retrieveAllCredentials() Error loading credentials"
                );
            });
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _getUserInfo() {
        return this._userService.logMeInBro({});
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}
module.exports = ManageOrgsComponent;
ManageOrgsComponent.$groupName = "Organization";
ManageOrgsComponent.$displayName = "Organizations";
ManageOrgsComponent.$inject = [
    "$teve.services.instance.Organization",
    "$teve.entities.RetentionPolicy",
    "$teve.services.instance.User",
    "$teve.userSession",
    "$teve.ui.UiUtils",
    "$teve.core",
    "$teve.logger",
    "$teve.router",
    "$teve.services.Hubby",
    "$teve.ui.userFeedback",
    "$teve.userLocalStorage",
    "$teve.storageFactory",
    "$teve.entities.SystemConfig",
    "$teve.entities.Credentials",
    "$teve.entities.Tenant",
];
